// @flow
import _ from 'lodash';

import ApiManager from './ApiManager';

// constants
import APIEndpoints from '../constants/APIEndpoints';

// api
import ApiPayment from './ApiPayment';
import ApiUser from './ApiUser';

import OrderApiErrors from '../utils/apiErrors/OrderApiErrors';

// entities
import Restaurant from '../entities/Restaurant';
import OrdersList from '../entities/order/OrdersList';
import Check from '../entities/Check';
import ChecksList from '../entities/ChecksList';
import User from '../entities/user/User';
import UsersList from '../entities/user/UsersList';
import ApiError from '../entities/ApiError';
import { OrderSettings, Order } from '../entities/order';

class ApiOrder {

  //
  // Orders List - On Going
  //

  /**
   * Retrieve the on going orders list for the restaurant.
  */
  static getOrdersOnGoingForRestaurant(restaurantId: string, success: Function, failure: Function) {

    const requestOptions = {
      endpoint: APIEndpoints.ORDERS_ON_GOING,
      query: {
        restaurantId: restaurantId,
        v2: true
      },
      success: (json: Object) => {
        const ordersList: OrdersList = new OrdersList();
        // OrdersList deserialize an array with the key 'orders' but we just receive an array.
        ordersList.fromJson(json);

        success(ordersList)
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }

  //
  // Orders List - By Modification Date
  //

  /**
  * Retrieve the orders list for the restaurant in the given timelapse.
  * It will returns all the orders with the modificationDate on this interval.
  *
  * @param fromTime
  * @param toTime
  */
  static getOrdersListByModificationDate(restaurantId: string, fromTime: number, toTime: number, success: Function, failure: Function) {
    const requestOptions = {
      endpoint: APIEndpoints.ORDERS,
      query: {
        restaurantId: restaurantId,
        fromTime,
        toTime,
        filterBy: 'modificationDate',
        v2: true,
        limit: 200
      },
      success: (json: Object) => {
        const ordersList: OrdersList = new OrdersList();
        // OrdersList deserialize an array with the key 'orders' but we just receive an array.
        ordersList.fromJson(json);

        success(ordersList);
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }


  //
  // Orders List - By Due Date
  //

  /**
  * Retrieve the orders history list for the restaurant in the given timelapse.
  * It will returns all the orders with the dueDate on this interval.
  *
  * @param fromTime
  * @param toTime
  */
  static getOrdersListByDueDate(restaurantId: string, fromTime: number, toTime: number, success: Function, failure: Function) {

    const requestOptions = {
      endpoint: APIEndpoints.ORDERS,
      query: {
        restaurantId: restaurantId,
        fromTime,
        toTime,
        filterBy: 'dueDate',
        v2: true,
        limit: 200
      },
      success: (json: Object) => {
        const ordersList: OrdersList = new OrdersList();
        // OrdersList deserialize an array with the key 'orders' but we just receive an array.
        ordersList.fromJson(json);

        success(ordersList)
      },
      failure: (apiError: ApiError) => {
        failure(apiError);
      }
    }

    ApiManager.get(requestOptions);
  }

    //
    // Order
    //

    /**
    * Post the given order
    */
    static postOrder(orderToCreate: Order, success: Function, failure: Function) {
      const endpoint = APIEndpoints.ORDERS;

      const requestOptions = {
        endpoint,
        headers: {
          'session-id': orderToCreate.sessionId || ''
        },
        data: orderToCreate,
        success: (json: Object) => {

          const order: Order = new Order();
          order.fromJson(json);

          // Retrieve checks objects
          ApiPayment.getChecks(
            [order.checkId],
            (checksList: ChecksList) => {

              const check = checksList.findById(order.checkId);
              if (!_.isNull(check)) {
                order.check = check.copy();
              }

              _.each(order.products, (product) => {
                product.prices = orderToCreate.getProductWithId(product.id).prices;
              });
              _.each(order.menus, (menu) => {
                menu.prices = orderToCreate.getMenuWithId(menu.id).prices;
              });
              order.mergeUiDatas(orderToCreate)

              success(order);
            }, (apiError: ApiError) => {
              failure(apiError);
            });
        },
        failure: (apiError: ApiError) => {
          apiError.localizedMessage = OrderApiErrors.post(apiError);
          failure(apiError);
        }
      }

      ApiManager.post(requestOptions);
    }

    /**
    * Post the given light order
    */
    static postOrderLight(orderToCreate: Order, email: string, apiKey: string, success: Function, failure: Function) {
      const endpoint = APIEndpoints.ORDERS;

      const requestOptions = {
        endpoint,
        headers: {
          Accept: 'application/json',
          Authorization: ApiManager.formatAuthorizationHeaderLight(email),
          'api-consumer': apiKey,
          'session-id': orderToCreate.sessionId || '',
        },
        data: orderToCreate,
        success: (json: Object) => {

          const order: Order = new Order();
          order.fromJson(json);

          // _.each(order.products, (product) => {
          //   product.prices = orderToCreate.getProductWithId(product.id).prices;
          // });
          // _.each(order.menus, (menu) => {
          //   menu.prices = orderToCreate.getMenuWithId(menu.id).prices;
          // });

          success(order);
        },
        failure: (apiError: ApiError) => {
          apiError.localizedMessage = OrderApiErrors.post(apiError);
          failure(apiError);
        }
      }

      ApiManager.post(requestOptions);
    }

    static postOrderPreview(orderToCreate: Order, success: Function, failure: Function, applyCartModifiers = true) {
      const endpoint = APIEndpoints.ORDERS_PREVIEW;

      const getMaxGrantableAmount = !_.isNull(orderToCreate.userAccountPayment) ? true : false
      const getAdmissionFeesAmount = !_.isNull(orderToCreate.userAccountPayment) ? true : false
      const verifyUserAccount = !_.isNull(orderToCreate.userAccountPayment) ? true : false

      const requestOptions = {
        endpoint,
        query: {
          applyCartModifiers,
          getMaxGrantableAmount,
          getAdmissionFeesAmount,
          verifyUserAccount,
        },
        headers: {
          'session-id': orderToCreate.sessionId || '',
        },
        data: orderToCreate,
        success: (json: Object) => {

          if (typeof json.succeeded !== 'undefined' && json.succeeded) {
            const order: Order = new Order();
            order.fromJson(json.order);

            // merge data
            order.populateListItemsCart(orderToCreate.listItemsCart);
            order.mergeUiDatas(orderToCreate)

            success(order, json.verifications);
          } else {
            failure();
          }
        },
        failure: (apiError: ApiError) => {
          failure(apiError);
        }
      }

      ApiManager.post(requestOptions);
    }


    /**
    * Put the given order
    */
    static putOrder(orderToUpdate: Order, success: Function, failure: Function) {
      const endpoint = APIEndpoints.ORDER.replace('{orderId}', orderToUpdate.id);

      const requestOptions = {
        endpoint,
        data: orderToUpdate,
        success: (json: Object) => {

          const order:Order = new Order();
          order.fromJson(json);

          // TODO: retrieve from the API ?
          order.check = orderToUpdate.check;
          order.user = orderToUpdate.user;

          success(order);
        },
        failure: (apiError: ApiError) => {
          apiError.localizedMessage = OrderApiErrors.put(apiError);
          failure(apiError);
        }
      }

      ApiManager.put(requestOptions);
    }

    //
    // Order Settings
    //

    /**
    * Put the order settings
    */
    static getOrderSettings(restaurantId: string, success: Function, failure: Function) {
      const endpoint = APIEndpoints.ORDER_SETTINGS.replace('{restaurantId}', restaurantId);

      const requestOptions = {
        endpoint,
        query: {},
        success: (json: Object) => {

          const orderSettings:OrderSettings = new OrderSettings();
          orderSettings.fromJson(json);

          success(orderSettings);
        },
        failure: (apiError: ApiError) => {
          failure(apiError);
        }
      }

      ApiManager.get(requestOptions);
    }

    /**
    * Put the order settings
    */
    static putOrderSettings(restaurantId: string, orderSettings: OrderSettings, success: Function, failure: Function) {
      const endpoint = APIEndpoints.ORDER_SETTINGS.replace('{restaurantId}', restaurantId);

      const requestOptions = {
        endpoint,
        data: orderSettings,
        success: (json: Object) => {

          const orderSettings:OrderSettings = new OrderSettings();
          orderSettings.fromJson(json);


          success(orderSettings);
        },
        failure: (apiError: ApiError) => {
          failure(apiError);
        }
      }

      ApiManager.put(requestOptions);
    }

  }

  export default ApiOrder;



// WEBPACK FOOTER //
// ./sdk/scripts/api/ApiOrder.js